import { useCallback, useState } from "react";

import { ApiBlue, MailBlue } from "@similarweb/icons/illustrations";
import { Flex } from "@similarweb/ui-components/foundations";

import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";

import { openSetupGuideEmail } from "../GovernanceUsersTable/buildSetupGuideMailtoUrl";

import { DeploymentOptionCard } from "./DeploymentOptionCard";
import { ITInstructionsModal } from "./ITInstructionsModal";

type SelfInstallContentProps = {
    notInstalledEmails: string[];
};

export const SelfInstallContent = ({ notInstalledEmails }: SelfInstallContentProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSendGuide = useCallback(() => {
        extensionManagementTrackingService.trackDeploymentSendGuide();
        void (async () => {
            try {
                await openSetupGuideEmail(notInstalledEmails);
            } catch (error) {
                trackError(
                    new SiError(
                        "Error sending guide email",
                        { category: errorCategoryMap.service, operation: "send-guide-email" },
                        { cause: error },
                    ),
                );
            }
        })();
    }, [notInstalledEmails]);

    return (
        <>
            <Flex gap="16px">
                <DeploymentOptionCard
                    icon={<MailBlue size={80} />}
                    title="si.management.extension.deployment.self_install.quick_option.title"
                    description="si.management.extension.deployment.self_install.quick_option.description"
                    buttonText="si.management.extension.deployment.self_install.send_guide_email"
                    buttonIcon="email"
                    onClick={handleSendGuide}
                    dataAutomation="extension-deployment-send-guide-card"
                    buttonDataAutomation="extension-deployment-send-guide-email"
                />

                <DeploymentOptionCard
                    icon={<ApiBlue size={80} />}
                    title="si.management.extension.deployment.self_install.recommended.title"
                    description="si.management.extension.deployment.self_install.recommended.description"
                    buttonText="si.management.extension.deployment.self_install.view_it_instructions"
                    buttonIcon="settings"
                    onClick={() => {
                        extensionManagementTrackingService.trackDeploymentViewItInstructions();
                        setIsModalOpen(true);
                    }}
                    dataAutomation="extension-deployment-it-enforce-card"
                    buttonDataAutomation="extension-deployment-view-it-instructions"
                />
            </Flex>

            <ITInstructionsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
};
